/*
 * Network paths — how traffic physically gets from one PoP to another.
 *
 * Source of truth for the Path Builder (homepage) and the /network schematic.
 * Topology confirmed by Sofia Connect, 2026-10-20 (protected pairs for
 * Sofia–Balchik, Sofia–Istanbul and Sofia–Athens added 2026-10-21):
 *
 *   Frankfurt   — direct terrestrial from Sofia, 3 diverse routes
 *   London      — via Frankfurt
 *   Istanbul    — direct terrestrial, 2 protected routes
 *   Athens      — direct terrestrial, 2 protected routes
 *   Tbilisi     — Sofia → Balchik (2 protected routes), Caucasus Cable System Balchik → Poti,
 *                 then terrestrial Poti → Tbilisi
 *   Yerevan     — two terrestrial routes: via Tbilisi, and direct from Poti
 *   Agarak      — via Yerevan, two terrestrial routes
 *   Dubai       — via partner
 *   Bulgarian Edge PoPs — from Sofia over the national backbone
 *
 * RTT values are INDICATIVE round-trip times on the fibre path, not live
 * measurements. They are the figures the site already quotes (configurator
 * latency matrix), checked against physics: fibre route length × ~10 µs/km
 * round trip, with route length ≈ 1.3–1.6 × great-circle distance on land and
 * 1,182 km for the Caucasus Cable System. Every value sits at or above that
 * physical floor. Confirmed per order.
 *
 * Public-internet comparison figures are typical server-to-server pings
 * published by WonderNetwork (wondernetwork.com/pings), sampled 6–20 Oct 2026.
 * They show what the same city pair looks like over the public internet,
 * where traffic often detours via Western Europe.
 */

export type Medium = "terrestrial" | "subsea" | "partner";

export interface PathNode {
  id: string;
  /** Transit-only nodes (e.g. the Poti landing) are not sellable PoPs. */
  transit?: boolean;
  label: { en: string; bg: string };
}

export interface Segment {
  a: string;
  b: string;
  medium: Medium;
  /** Indicative round-trip time for this segment, ms. */
  rttMs: number;
  /** Number of physically diverse routes on this segment, when published. */
  routes?: number;
  /** Corridor this segment belongs to (drives colour on the schematic). */
  corridor: CorridorId;
  note?: { en: string; bg: string };
}

export type CorridorId = "west" | "south" | "caucasus" | "gulf" | "bulgaria";

export const CORRIDOR_INFO: Record<
  CorridorId,
  { name: { en: string; bg: string }; color: string; blurb: { en: string; bg: string } }
> = {
  west: {
    name: { en: "Western Europe", bg: "Западна Европа" },
    color: "#3D8BFD",
    blurb: {
      en: "Sofia to Frankfurt on three diverse terrestrial routes, onward to London.",
      bg: "София–Франкфурт по три независими наземни трасета, нататък към Лондон.",
    },
  },
  south: {
    name: { en: "Aegean & Bosphorus", bg: "Егея и Босфора" },
    color: "#00B894",
    blurb: {
      en: "Sofia to Istanbul and to Athens, each on two protected terrestrial routes.",
      bg: "София–Истанбул и София–Атина, всяко по две защитени наземни трасета.",
    },
  },
  caucasus: {
    name: { en: "Black Sea & Caucasus", bg: "Черно море и Кавказ" },
    color: "#A855F7",
    blurb: {
      en: "Two protected routes Sofia–Balchik, Caucasus Cable System to Poti, then terrestrial to Tbilisi, Yerevan and Agarak.",
      bg: "Две защитени трасета София–Балчик, Caucasus Cable System до Поти, после наземно до Тбилиси, Ереван и Агарак.",
    },
  },
  gulf: {
    name: { en: "Gulf (partner)", bg: "Залива (партньор)" },
    color: "#94A3B8",
    blurb: {
      en: "Dubai is reached through a long-haul partner.",
      bg: "До Дубай стигаме чрез партньор за дълги разстояния.",
    },
  },
  bulgaria: {
    name: { en: "Bulgarian Edge", bg: "Българска мрежа" },
    color: "#14B8A6",
    blurb: {
      en: "Nine regional PoPs on the national backbone, all homed on Sofia.",
      bg: "Девет регионални PoP-а на националната магистрала, вързани към София.",
    },
  },
};

export const PATH_NODES: readonly PathNode[] = [
  { id: "sofia", label: { en: "Sofia", bg: "София" } },
  { id: "frankfurt", label: { en: "Frankfurt", bg: "Франкфурт" } },
  { id: "london", label: { en: "London", bg: "Лондон" } },
  { id: "istanbul", label: { en: "Istanbul", bg: "Истанбул" } },
  { id: "athens", label: { en: "Athens", bg: "Атина" } },
  { id: "balchik", label: { en: "Balchik", bg: "Балчик" } },
  { id: "poti", transit: true, label: { en: "Poti", bg: "Поти" } },
  { id: "tbilisi", label: { en: "Tbilisi", bg: "Тбилиси" } },
  { id: "yerevan", label: { en: "Yerevan", bg: "Ереван" } },
  { id: "agarak", label: { en: "Agarak", bg: "Агарак" } },
  { id: "dubai", label: { en: "Dubai", bg: "Дубай" } },
  { id: "plovdiv", label: { en: "Plovdiv", bg: "Пловдив" } },
  { id: "stara-zagora", label: { en: "Stara Zagora", bg: "Стара Загора" } },
  { id: "burgas", label: { en: "Burgas", bg: "Бургас" } },
  { id: "varna", label: { en: "Varna", bg: "Варна" } },
  { id: "dobrich", label: { en: "Dobrich", bg: "Добрич" } },
  { id: "montana", label: { en: "Montana", bg: "Монтана" } },
  { id: "pleven", label: { en: "Pleven", bg: "Плевен" } },
  { id: "blagoevgrad", label: { en: "Blagoevgrad", bg: "Благоевград" } },
];

export const SEGMENTS: readonly Segment[] = [
  { a: "sofia", b: "frankfurt", medium: "terrestrial", rttMs: 24.5, routes: 3, corridor: "west" },
  { a: "frankfurt", b: "london", medium: "terrestrial", rttMs: 10.5, corridor: "west" },
  { a: "sofia", b: "istanbul", medium: "terrestrial", rttMs: 12, routes: 2, corridor: "south" },
  { a: "sofia", b: "athens", medium: "terrestrial", rttMs: 12, routes: 2, corridor: "south" },
  { a: "sofia", b: "balchik", medium: "terrestrial", rttMs: 6, routes: 2, corridor: "bulgaria" },
  {
    a: "balchik",
    b: "poti",
    medium: "subsea",
    rttMs: 15,
    corridor: "caucasus",
    note: { en: "Caucasus Cable System, 1,182 km", bg: "Caucasus Cable System, 1182 км" },
  },
  { a: "poti", b: "tbilisi", medium: "terrestrial", rttMs: 4, corridor: "caucasus" },
  { a: "poti", b: "yerevan", medium: "terrestrial", rttMs: 7, corridor: "caucasus" },
  { a: "tbilisi", b: "yerevan", medium: "terrestrial", rttMs: 3.5, corridor: "caucasus" },
  { a: "yerevan", b: "agarak", medium: "terrestrial", rttMs: 4.3, routes: 2, corridor: "caucasus" },
  { a: "tbilisi", b: "dubai", medium: "partner", rttMs: 58, corridor: "gulf" },
  { a: "sofia", b: "plovdiv", medium: "terrestrial", rttMs: 2, corridor: "bulgaria" },
  { a: "sofia", b: "stara-zagora", medium: "terrestrial", rttMs: 3, corridor: "bulgaria" },
  { a: "sofia", b: "burgas", medium: "terrestrial", rttMs: 5, corridor: "bulgaria" },
  { a: "sofia", b: "varna", medium: "terrestrial", rttMs: 5, corridor: "bulgaria" },
  { a: "sofia", b: "dobrich", medium: "terrestrial", rttMs: 6, corridor: "bulgaria" },
  { a: "sofia", b: "montana", medium: "terrestrial", rttMs: 2, corridor: "bulgaria" },
  { a: "sofia", b: "pleven", medium: "terrestrial", rttMs: 3, corridor: "bulgaria" },
  { a: "sofia", b: "blagoevgrad", medium: "terrestrial", rttMs: 2, corridor: "bulgaria" },
];

/**
 * Number of physically diverse ways to reach a node from Sofia, where it
 * differs from what the segment list implies (Yerevan: via Tbilisi, or
 * direct from Poti).
 */
export const DIVERSE_APPROACHES: Readonly<Record<string, number>> = {
  frankfurt: 3,
  yerevan: 2,
  agarak: 2,
};

/** Typical public-internet RTT from Sofia (WonderNetwork, Oct 2026). */
export const PUBLIC_INTERNET_RTT_FROM_SOFIA: Readonly<Record<string, number>> = {
  frankfurt: 30,
  london: 42,
  istanbul: 16,
  athens: 15,
  tbilisi: 75,
  yerevan: 67,
  dubai: 286,
};

export const PUBLIC_INTERNET_SOURCE = {
  name: "WonderNetwork",
  url: "https://wondernetwork.com/pings/Sofia",
  period: { en: "6–20 Oct 2026", bg: "6–20 окт. 2026" },
};

/* ─── Shortest path (Dijkstra on RTT) ─── */

export interface PathResult {
  nodes: string[];
  segments: Segment[];
  rttMs: number;
  viaPartner: boolean;
}

function neighbours(id: string): { to: string; seg: Segment }[] {
  const out: { to: string; seg: Segment }[] = [];
  for (const s of SEGMENTS) {
    if (s.a === id) out.push({ to: s.b, seg: s });
    else if (s.b === id) out.push({ to: s.a, seg: s });
  }
  return out;
}

export function shortestPath(from: string, to: string): PathResult | null {
  if (from === to) return null;
  const dist = new Map<string, number>([[from, 0]]);
  const prev = new Map<string, { node: string; seg: Segment }>();
  const open = new Set<string>([from]);
  const done = new Set<string>();
  while (open.size) {
    let cur = "";
    let best = Infinity;
    for (const n of open) {
      const d = dist.get(n) ?? Infinity;
      if (d < best) {
        best = d;
        cur = n;
      }
    }
    open.delete(cur);
    if (cur === to) break;
    done.add(cur);
    for (const { to: nb, seg } of neighbours(cur)) {
      if (done.has(nb)) continue;
      const nd = best + seg.rttMs;
      if (nd < (dist.get(nb) ?? Infinity)) {
        dist.set(nb, nd);
        prev.set(nb, { node: cur, seg });
        open.add(nb);
      }
    }
  }
  if (!dist.has(to)) return null;
  const nodes = [to];
  const segments: Segment[] = [];
  let c = to;
  while (c !== from) {
    const p = prev.get(c)!;
    segments.unshift(p.seg);
    nodes.unshift(p.node);
    c = p.node;
  }
  return {
    nodes,
    segments,
    rttMs: Math.round((dist.get(to) ?? 0) * 10) / 10,
    viaPartner: segments.some((s) => s.medium === "partner"),
  };
}

/** Indicative RTT from Sofia to a PoP, for tables and tooltips. */
export function rttFromSofia(id: string): number | null {
  if (id === "sofia") return 0;
  return shortestPath("sofia", id)?.rttMs ?? null;
}

export function nodeLabel(id: string, lang: "en" | "bg"): string {
  return PATH_NODES.find((n) => n.id === id)?.label[lang] ?? id;
}
